import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import type { SharedRebateProgramsStats } from "../../../api/generated";

import { showErrorToast, showSuccessToast } from "../../../Toast";
import { type ReloadScope, ReloadScopeProvider } from "../../periodLoading";
import { getAllTimeDateRange } from "../../providers/getDefaultDateRange";
import {
  buildRosterWithCreated,
  collectPrograms,
  type CreatedState,
  type CreateProgramApiOptions,
  type CreateProgramInput,
  type CreateProgramOutcome,
  type CreateProgramStepUp,
  type Hoid,
  type PartnerSpec,
  type PendingCreateResume,
  type ProgramDetailPatch,
  type SubIB,
} from "../data";
import { computeProgramsPage } from "./computeProgramsPage";
import { normalizeSearchTerm } from "./normalizeSearchTerm";
import {
  BaseSharedRebateProgramsProvider,
  type SharedRebateProgramsParams,
  useBaseSharedRebateProgramsContext,
} from "./SharedRebateProgramsContext";

export type SharedRebateProgramsOption = { value: string; label: string };

export type SharedRebateProgramsResources = {
  stats: {
    api: (
      params: SharedRebateProgramsParams,
    ) => SharedRebateProgramsStats | Promise<SharedRebateProgramsStats>;
    shouldAutoRun: (
      next: SharedRebateProgramsParams,
      prev: SharedRebateProgramsParams | null,
    ) => boolean;
  };
  /**
   * Export CSV. Deliberately has no `shouldAutoRun` — it is a **manual**
   * resource, run only via `runResource("csv")` from the Export button, never
   * on a filter change. The api performs the download itself and resolves with
   * nothing; the view only reads its status for the in-progress state.
   */
  csv: {
    api: (params: SharedRebateProgramsParams) => Promise<void>;
  };
  /**
   * Table (list) API. Injected by the portal (real API adapter over
   * useGetRebatePrograms); when omitted, the table falls back to the mock
   * roster (data.ts) via computeProgramsPage — Storybook / vibe-coding /
   * tests work without a portal hook.
   */
  table?: {
    api: (
      params: SharedRebateProgramsParams,
    ) => Promise<{ rows: Hoid[]; totalCount: number }>;
  };
  /**
   * Filter dropdown options. Injected by the portal (real API adapter over
   * useGetRebateProgramsFilterOptions); when omitted, options are derived
   * from the mock roster.
   */
  filterOptions?: {
    api: () => Promise<{
      subIb: SharedRebateProgramsOption[];
      status: SharedRebateProgramsOption[];
    }>;
  };
  /**
   * Per-program detail patch, scoped to the drawer's own date-range chip
   * rather than the page's date filter — Total earning, Your payout, Sub IB
   * payout, and Trading volume (Clients/Client accounts are all-time by
   * design and always read off the table row instead). Injected by the
   * portal (real API adapter over useGetRebateProgramDetail); when omitted,
   * the drawer keeps whichever values the row already carries.
   */
  programDetail?: {
    /** `programId` is the program's internal id (Hoid.programId), not its displayed HOID. */
    api: (
      programId: string,
      dateRange: { from: Date | null; to: Date | null },
    ) => Promise<ProgramDetailPatch>;
  };
  rejectionNotification?: {
    dismiss: (notificationId: string) => Promise<void>;
  };
  /**
   * Archive-single mutation. Injected by the portal (real API adapter over
   * useArchiveSharedRebateProgram); when omitted, archiving only flips local
   * state (Storybook / vibe-coding / tests) — see `archiveProgram`.
   */
  archive?: {
    /** `programId` is the program's internal id (Hoid.programId), not its displayed HOID. */
    api: (programId: string, options?: ArchiveApiOptions) => Promise<void>;
  };
  /**
   * Create-program mutation. Injected by the portal (real API adapter over
   * useCreateRebateProgram); when omitted, the wizard falls back to
   * mockSubmitProgram — Storybook / vibe-coding / tests work without a portal
   * hook, exactly as `table` and `archive` do.
   */
  create?: {
    api: (
      input: CreateProgramInput,
      options?: CreateProgramApiOptions,
    ) => Promise<CreateProgramOutcome>;
    stepUp?: CreateProgramStepUp;
  };
  stepUp?: StepUpResource;
  /**
   * Master status, read on demand when the user asks to create a program — it
   * answers all three questions that gate the wizard (rebate account, current
   * program count, configured limit) in one call. Injected by the portal (real
   * API adapter over useGetMasterStatusHook); when omitted, `requestCreateProgram`
   * opens the wizard unconditionally so Storybook / vibe-coding / tests work
   * without a portal hook.
   *
   * Deliberately fetched per click rather than cached: the count must be fresh
   * after an archive, which frees a slot.
   *
   * With `phase2Enabled` it is also read once on mount, for
   * `totalRebatePrograms` — what tells "no programs yet" apart from "the filter
   * matched nothing".
   */
  status?: {
    api: () => Promise<MasterStatus>;
  };
};

export type StepUpResource = {
  getGate: () => Promise<"verify_email" | "none">;
  email?: string;
  sendVerificationEmail: () => Promise<"sent" | "already_verified">;
  getEmailVerified: () => Promise<boolean>;
  markEmailVerified: () => void;
  pendingCreateResume?: PendingCreateResume | null;
};

export type ArchiveApiOptions = {
  resumed?: boolean;
  fromDrawer?: boolean;
  hoid?: Hoid;
  /** Aborted when the user leaves the redirect or "Closing your program…" screen (Back / Close). */
  signal?: AbortSignal;
  /** Called just before going to Auth0 for the OTP — only then is the redirect screen shown. */
  onRedirect?: () => void;
};

/** Answer to `resources.status.api` — mirrors GET /v1/master/status. */
export type MasterStatus = {
  activeRebatePrograms: number;
  totalRebatePrograms: number;
  hasRebateAccount: boolean;
  /**
   * Absent when the API predates the field; the default-set gate only fires on
   * an explicit `false`, so an older API never blocks creation.
   */
  hasDefaultSetId?: boolean;
  maxRebatePrograms: number;
  /**
   * The Master's own rebate account login, shown read-only on the Create
   * Program wizard's Review step. Null when `hasRebateAccount` is false.
   * Named to match the wire field (`GetStatusOutput.Body.latestRebateAccount`)
   * 1:1 so adapters don't have to rename it.
   */
  latestRebateAccount: string | null;
};

const PAGE_SIZE = 10;

// The table always refetches on any params change (search/filter/sort/page/
// dateRange all matter to it) — stable module reference so it doesn't itself
// make the base provider's memoized resources object below unstable.
const ALWAYS_AUTO_RUN = () => true;

// Program status is a fixed, two-value enum (Active/Archived — shown as
// "Closed"), not data — the filter list is static rather than sourced from
// `filterOptions.api`'s `status` field, which would (a) drop an option
// whenever no program currently has that status and (b) drift if the backend
// ever returns different values/casing. `value` must stay the raw backend
// enum ("Active"/"Archived") since it's what's sent as the filter param and
// matched in `statusLabel` below — only the displayed `label` differs.
const STATIC_STATUS_OPTIONS: SharedRebateProgramsOption[] = [
  { value: "Active", label: "Active" },
  { value: "Archived", label: "Archived" },
];

/**
 * Page-local UI state not modeled by createReportContext (wizard-open flag,
 * drawer-selected program id, session-created programs) — kept alongside
 * the report engine per the createReportContext adapter pattern (see
 * PerformanceReportProvider.tsx).
 */
type SharedRebateProgramsUiState = {
  wizardOpen: boolean;
  openWizard: () => void;
  closeWizard: () => void;
  wizardPresetSubId: string | null;
  selectedProgramId: string | null;
  openProgramDetails: (id: string) => void;
  closeProgramDetails: () => void;
  registerCreatedProgram: (hoid: Hoid, partnerSpecs: PartnerSpec[]) => void;
  /** Rejects on API failure — callers (ArchiveProgramModal) keep local state untouched and let the user retry. */
  archiveProgram: (hoid: Hoid, options?: ArchiveApiOptions) => Promise<void>;
  requestArchiveProgram: (hoid: Hoid, proceed: (hoid: Hoid) => void) => void;
  filterOpen: boolean;
  openFilter: () => void;
  closeFilter: () => void;
  capModalOpen: boolean;
  openCapModal: () => void;
  closeCapModal: () => void;
  verifyEmailOpen: boolean;
  openVerifyEmail: () => void;
  closeVerifyEmail: () => void;
  verifyEmail: {
    email?: string;
    send: () => Promise<"sent" | "already_verified">;
    getVerified: () => Promise<boolean>;
    onVerified: () => void;
  } | null;
  /**
   * The Master's configured program limit, from the last status read — what the
   * cap modal renders. Null until a status call resolves, which is also the only
   * way the wizard opens, so every path that can raise the cap modal has already
   * set it.
   */
  capLimit: number | null;
  /** A status read is in flight — the "New rebate program" entry points disable. */
  statusPending: boolean;
  /** The Master's own rebate account is inactive, so creation is blocked. */
  noRebateAccountOpen: boolean;
  closeNoRebateAccount: () => void;
  /** The Master's effective tier has no usable default set yet, so creation is blocked. */
  defaultSetPendingOpen: boolean;
  closeDefaultSetPending: () => void;
  /** The master-status pre-flight failed, so creation could not even be attempted. */
  createErrorOpen: boolean;
  closeCreateError: () => void;
  /**
   * Opened up-front by `requestCreateProgram`, and again if the create call
   * itself comes back 422 `no_rebate_account` — the pre-flight flag can be stale
   * or absent, so the API's answer is the authoritative one.
   */
  openNoRebateAccount: () => void;
  /**
   * Reads master status, then opens the wizard — or a blocking modal instead
   * when the Master has no active rebate account or is at their program limit.
   */
  requestCreateProgram: (presetSubId?: string) => void;
  /** Real create-program mutation (see `SharedRebateProgramsResources.create`); undefined in mock mode. */
  createProgram?: (
    input: CreateProgramInput,
    options?: CreateProgramApiOptions,
  ) => Promise<CreateProgramOutcome>;
  createStepUp?: CreateProgramStepUp;
  pendingCreateResume: PendingCreateResume | null;
  totalProgramsUnfiltered: number;
  /**
   * The empty state is split into "no programs" and "filtered to nothing" —
   * true only with `phase2Enabled` and an injected `status` resource to read
   * the total from.
   */
  splitEmptyState: boolean;
  /** `MasterStatus.totalRebatePrograms`; null until read, or when the read failed. */
  totalRebatePrograms: number | null;
  /** The first `totalRebatePrograms` read is still in flight. */
  totalRebateProgramsPending: boolean;
  refreshTotalRebatePrograms: () => void;
  /** Full (unfiltered) program list — used to derive CSV export rows. */
  allPrograms: Hoid[];
  /** Full Sub IB roster (session-created subs included) — resolves wizardPresetSubId. */
  roster: SubIB[];
  /**
   * The Master's own rebate account, shown read-only on the wizard's Review
   * step — read off `MasterStatus.latestRebateAccount`, the same
   * `resources.status.api()` call `requestCreateProgram` uses to gate the
   * wizard. Null until that call resolves, or permanently when no `status`
   * resource is injected (Storybook / vibe-coding / tests).
   */
  masterRebateAccount: string | null;
  /** Real per-program detail patch (see `SharedRebateProgramsResources.programDetail`); undefined in mock mode. */
  fetchProgramDetailPatch?: (
    programId: string,
    dateRange: { from: Date | null; to: Date | null },
  ) => Promise<ProgramDetailPatch>;
  dismissRejectedNotification?: (notificationId: string) => Promise<void>;
};

const SharedRebateProgramsUiContext =
  createContext<SharedRebateProgramsUiState | null>(null);

function useSharedRebateProgramsUi(): SharedRebateProgramsUiState {
  const ctx = useContext(SharedRebateProgramsUiContext);
  if (!ctx) {
    throw new Error(
      "useSharedRebateProgramsUi must be used within SharedRebateProgramsProvider",
    );
  }
  return ctx;
}

/**
 * Splits "something is reloading" into the two shapes the design asks for, and
 * publishes them to the pieces that care.
 *
 * - **Payout period changed** → only the figures that actually move with it
 *   reload (Total earning, Your payout, and the two money stat cards). Rows,
 *   entity columns, Last payment (an all-time value, never date-scoped) and
 *   the all-time Clients count stay put.
 * - **Anything else changed** — search, a filter, sort, page — → the rows are
 *   replaced wholesale, so the whole table and every stat card reload.
 * - **First load** → treated as "anything else": there is nothing on screen to
 *   preserve.
 *
 * Lives inside the base provider because that is the only place both halves are
 * readable: what was *requested* (`state`) and whether the resources have caught
 * up (`resources`). Neither the outer UI provider nor the consumer hook sees both.
 *
 * Derived from a requested-vs-loaded key comparison rather than driven by wrapped
 * setters, so every route that changes a param is covered without having to
 * remember to announce itself.
 */
const ReloadScopeBridge = ({ children }: { children: ReactNode }) => {
  const { state, resources, runResource } =
    useBaseSharedRebateProgramsContext();

  // Browser Back from an external redirect (the step-up OTP page) can restore
  // this page from the bfcache: the frozen page comes back with stale or empty
  // table/stats, and because no param changed there is nothing to trigger the
  // report engine's auto-run. `pageshow` with `persisted` fires on that restore
  // — refetch both resources explicitly so the table repopulates. (This runs
  // through runResource, the report engine's own refetch, not TanStack Query,
  // which is why an app-level invalidateQueries could not recover it.)
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      runResource("table");
      runResource("stats");
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, [runResource]);

  const dateKey = `${state.dateRange.from?.getTime() ?? "null"}_${
    state.dateRange.to?.getTime() ?? "null"
  }`;
  // Used only to *classify* a reload, never to trigger one. The table watches
  // every param, so its last-settled snapshot is the reference for "what did the
  // user change".
  const paramsKey = JSON.stringify({
    // Normalised, matching deriveParams. On the raw value, typing a trailing
    // space would change this key without changing any request, so the next date
    // change would compare against a key nothing had settled on and be
    // misclassified as a full reload.
    search: normalizeSearchTerm(state.searchQuery),
    selected: state.selected,
    sortBy: state.sortBy,
    sortOrder: state.sortOrder,
    page: state.page,
  });

  const [settled, setSettled] = useState<{
    date: string;
    params: string;
  } | null>(null);

  const tableUpdatedAt = resources.table.updatedAt;
  // The keys are deliberately absent from the dependency list: this must fire when
  // the table *settles*, stamping whichever params the freshly-arrived data is for.
  // Including them would stamp the new keys the instant the user changed
  // something, before any request had returned.
  //
  // The `undefined` guard is load-bearing. The factory drops `updatedAt` on both
  // the loading and error branches, so without it this fires at the *start* of
  // every refetch — stamping the new keys immediately and classifying every period
  // change as a full reload. Only a success carries a timestamp.
  useEffect(() => {
    if (tableUpdatedAt === undefined) return;
    setSettled({ date: dateKey, params: paramsKey });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableUpdatedAt]);

  /**
   * Which resources are actually in flight — read from the factory rather than
   * inferred from the params.
   *
   * This matters because resources watch different params: `stats` ignores sort
   * and page, since the KPIs are totals across every page. Deriving "stats is
   * reloading" from a params key would mark it stale on a page change, and since
   * it never refetches, the cards would skeleton forever. The factory already
   * knows which resources it re-ran, so ask it.
   *
   * It also removes any risk of stranding: these are live statuses, so an error
   * or an abandoned fetch clears them on its own.
   */
  const tableInFlight = resources.table.status === "loading";
  const statsInFlight = resources.stats.status === "loading";

  const scope: ReloadScope = useMemo(() => {
    // Nothing settled yet → first load: there is nothing on screen to preserve.
    const periodOnly =
      settled !== null &&
      settled.params === paramsKey &&
      settled.date !== dateKey;
    return {
      full: {
        table: tableInFlight && !periodOnly,
        stats: statsInFlight && !periodOnly,
      },
      period: {
        table: tableInFlight && periodOnly,
        stats: statsInFlight && periodOnly,
      },
    };
  }, [settled, dateKey, paramsKey, tableInFlight, statsInFlight]);

  return <ReloadScopeProvider scope={scope}>{children}</ReloadScopeProvider>;
};

export const SharedRebateProgramsProvider = ({
  children,
  resources,
  phase2Enabled = false,
}: {
  children: ReactNode;
  /** Real in apps/portal; mock fallbacks apply per-field in Storybook/tests/vibe-coding. */
  resources: SharedRebateProgramsResources;
  phase2Enabled?: boolean;
}) => {
  const { t } = useTranslation();
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardPresetSubId, setWizardPresetSubId] = useState<string | null>(
    null,
  );
  const [selectedProgramId, setSelectedProgramId] = useState<string | null>(
    null,
  );
  const [filterOpen, setFilterOpen] = useState(false);
  const [capModalOpen, setCapModalOpen] = useState(false);
  const [verifyEmailOpen, setVerifyEmailOpen] = useState(false);
  const [capLimit, setCapLimit] = useState<number | null>(null);
  const [statusPending, setStatusPending] = useState(false);
  const [noRebateAccountOpen, setNoRebateAccountOpen] = useState(false);
  const [defaultSetPendingOpen, setDefaultSetPendingOpen] = useState(false);
  const [createErrorOpen, setCreateErrorOpen] = useState(false);
  /**
   * Set from the same `resources.status.api()` read `requestCreateProgram`
   * already does to gate the wizard — GET /v1/master/status carries the
   * Master's own rebate account login alongside the cap/eligibility fields.
   * Stays null with no `status` resource injected (Storybook / vibe-coding /
   * tests), where Review then shows its own placeholder.
   */
  const [masterRebateAccount, setMasterRebateAccount] = useState<string | null>(
    null,
  );
  const [created, setCreated] = useState<CreatedState>({
    subs: [],
    programs: [],
  });
  const [archivedIds, setArchivedIds] = useState<Set<string>>(new Set());

  const registerCreatedProgram = useCallback(
    (hoid: Hoid, partnerSpecs: PartnerSpec[]) => {
      setCreated((prev) => {
        const subs = [...prev.subs];
        partnerSpecs.forEach((spec) => {
          if (!spec.isNew) return;
          if (subs.some((s) => s.id === spec.canonicalId)) return;
          subs.push({
            id: spec.canonicalId,
            name: spec.name,
            email: spec.email,
            kycStatus: spec.kycStatus,
            partnerId: spec.partnerId,
            comment: spec.comment ?? "",
            addedAt: new Date().toISOString().slice(0, 10),
            hoids: [],
            uniqueClientsAgg: 0,
            aggregateStatus: "Active",
            rebateSplitMin: 0,
            rebateSplitMax: 0,
            lastPayment: null,
            clientAccounts: 0,
            tradingVolume: 0,
            totalRebate: 0,
            myPayout: 0,
            aggregateUniqueClients: 0,
            aggregateSubIbPayout: 0,
            sharedProgramCount: 0,
          });
        });
        return { subs, programs: [...prev.programs, hoid] };
      });
    },
    [],
  );

  const roster = useMemo(() => {
    const base = buildRosterWithCreated(created);
    if (archivedIds.size === 0) return base;
    return base.map((sub) => ({
      ...sub,
      hoids: sub.hoids.map((hoid) =>
        archivedIds.has(hoid.id)
          ? { ...hoid, status: "Archived" as const }
          : hoid,
      ),
    }));
  }, [created, archivedIds]);
  const allPrograms = useMemo(() => collectPrograms(roster), [roster]);

  const archiveResource = resources.archive;
  const archiveProgram = useCallback(
    async (hoid: Hoid, options?: ArchiveApiOptions) => {
      if (archiveResource) {
        // Archive keys off the program's internal id (hoid.programId), not the
        // displayed HOID — see get_program_detail/handler.go. The mock leaves
        // programId unset, where id doubles as both.
        await archiveResource.api(hoid.programId ?? hoid.id, {
          ...options,
          hoid,
        });
      }
      // The detail drawer stays open on archive — only the modal closes.
      setArchivedIds((prev) => new Set(prev).add(hoid.id));
      // hoid.id is the displayed HOID (offer_id), not the internal programId.
      showSuccessToast({
        title: t(
          "subIBReport_archiveSuccess",
          "Rebate program HOID {{hoid}} closed",
          { hoid: hoid.id },
        ),
      });
    },
    [archiveResource, t],
  );

  const statusApi = resources.status?.api;

  const splitEmptyState = phase2Enabled && Boolean(statusApi);
  const [totalRebatePrograms, setTotalRebatePrograms] = useState<number | null>(
    null,
  );
  const [totalRebateProgramsPending, setTotalRebateProgramsPending] =
    useState(splitEmptyState);

  // BackOffice rebuilds `resources` on every render, so the mount-time read
  // must not key off `statusApi`'s identity or it would refetch each time.
  const statusApiRef = useRef(statusApi);
  useEffect(() => {
    statusApiRef.current = statusApi;
  }, [statusApi]);

  const refreshTotalRebatePrograms = useCallback(() => {
    statusApiRef
      .current?.()
      .then((status) => setTotalRebatePrograms(status.totalRebatePrograms))
      .catch(() => setTotalRebatePrograms(null))
      .finally(() => setTotalRebateProgramsPending(false));
  }, []);

  useEffect(() => {
    if (!splitEmptyState) return;
    refreshTotalRebatePrograms();
  }, [splitEmptyState, refreshTotalRebatePrograms]);

  const runCreateGates = useCallback(
    (presetSubId?: string) => {
      const openWizardFor = (id?: string) => {
        setWizardPresetSubId(id ?? null);
        setWizardOpen(true);
      };

      // No status resource injected (Storybook / vibe-coding / tests): there is
      // nothing to gate on, so the wizard opens as it always did.
      if (!statusApi) {
        openWizardFor(presetSubId);
        return;
      }

      setStatusPending(true);
      void statusApi()
        .then((status) => {
          setCapLimit(status.maxRebatePrograms);
          setMasterRebateAccount(status.latestRebateAccount);

          // The Master's own rebate account gates the whole flow — there is
          // nowhere for a new program's earnings to be paid without it. Checked
          // before the cap so a Master who is both blocked and at the limit sees
          // the account problem, which is the one they have to fix first.
          if (!status.hasRebateAccount) {
            setNoRebateAccountOpen(true);
            return;
          }
          // The limit is the Master's total across every Sub IB, so it is not
          // conditional on which Sub IB (if any) this request targets.
          if (status.activeRebatePrograms >= status.maxRebatePrograms) {
            setCapModalOpen(true);
            return;
          }
          if (status.hasDefaultSetId === false) {
            setDefaultSetPendingOpen(true);
            return;
          }
          openWizardFor(presetSubId);
        })
        .catch(() => {
          // Every failure class lands here, 403 (not an eligible Master)
          // included: without a status answer we know neither the rebate-account
          // state nor the program count, so there is no branch to choose and one
          // generic message is all we can honestly show.
          setCreateErrorOpen(true);
        })
        .finally(() => setStatusPending(false));
    },
    [statusApi],
  );

  const stepUpResource = resources.stepUp;
  const getStepUpGate = stepUpResource?.getGate;
  const gatedContinuation = useRef<(() => void) | null>(null);

  const requestCreateProgram = useCallback(
    (presetSubId?: string) => {
      if (!getStepUpGate) {
        runCreateGates(presetSubId);
        return;
      }

      setStatusPending(true);
      void getStepUpGate()
        .catch(() => "none" as const)
        .then((gate) => {
          if (gate === "verify_email") {
            gatedContinuation.current = () => runCreateGates(presetSubId);
            setStatusPending(false);
            setVerifyEmailOpen(true);
            return;
          }
          runCreateGates(presetSubId);
        });
    },
    [getStepUpGate, runCreateGates],
  );

  const requestArchiveProgram = useCallback(
    (hoid: Hoid, proceed: (hoid: Hoid) => void) => {
      if (!getStepUpGate) {
        proceed(hoid);
        return;
      }
      void getStepUpGate()
        .catch(() => "none" as const)
        .then((gate) => {
          if (gate === "verify_email") {
            gatedContinuation.current = () => proceed(hoid);
            setVerifyEmailOpen(true);
            return;
          }
          proceed(hoid);
        });
    },
    [getStepUpGate],
  );

  const verifyEmail = useMemo<SharedRebateProgramsUiState["verifyEmail"]>(
    () =>
      stepUpResource
        ? {
            email: stepUpResource.email,
            send: stepUpResource.sendVerificationEmail,
            getVerified: stepUpResource.getEmailVerified,
            onVerified: () => {
              stepUpResource.markEmailVerified();
              setVerifyEmailOpen(false);
              const proceed = gatedContinuation.current;
              gatedContinuation.current = null;
              proceed?.();
            },
          }
        : null,
    [stepUpResource],
  );

  const resumeFromResource = stepUpResource?.pendingCreateResume ?? null;
  const [pendingCreateResume, setPendingCreateResume] =
    useState<PendingCreateResume | null>(null);
  useEffect(() => {
    if (!resumeFromResource) return;
    // oxlint-disable-next-line react/set-state-in-effect
    setPendingCreateResume(resumeFromResource);
    setWizardPresetSubId(null);
    setWizardOpen(true);
  }, [resumeFromResource]);

  const deriveParams = useCallback(
    (state: {
      selected: { subIb: string[] | null; status: string[] | null };
      searchQuery: string;
      dateRange: { from: Date | null; to: Date | null };
      sortBy: string | null;
      sortOrder: "asc" | "desc" | null;
      page: number;
      pageSize: number;
    }) => ({
      selected: state.selected,
      search: normalizeSearchTerm(state.searchQuery),
      dateRange: state.dateRange,
      sortBy: state.sortBy,
      sortOrder: state.sortOrder,
      page: state.page,
      pageSize: state.pageSize,
    }),
    [],
  );

  const tableResource = resources.table;
  const tableApi = useCallback(
    (params: SharedRebateProgramsParams) =>
      tableResource
        ? tableResource.api(params)
        : Promise.resolve(computeProgramsPage(allPrograms, params)),
    [allPrograms, tableResource],
  );

  const statsApi = useCallback(
    (params: SharedRebateProgramsParams) =>
      Promise.resolve(resources.stats.api(params)),
    [resources],
  );

  const statsShouldAutoRun = resources.stats.shouldAutoRun;

  const csvApi = useCallback(
    (params: SharedRebateProgramsParams) => resources.csv.api(params),
    [resources],
  );

  const filterOptionsApi = resources.filterOptions?.api;
  const [remoteSelectOptions, setRemoteSelectOptions] = useState<{
    subIb: SharedRebateProgramsOption[];
    status: SharedRebateProgramsOption[];
  } | null>(null);

  useEffect(() => {
    if (!filterOptionsApi) return;
    let alive = true;
    filterOptionsApi()
      .then((opts) => {
        if (alive) setRemoteSelectOptions(opts);
      })
      .catch(() => {
        // Falls back to an empty subIb list below (selectOptions) — status
        // is static regardless, see STATIC_STATUS_OPTIONS. Surface the
        // failure explicitly rather than leaving the Sub IB dropdown
        // silently empty with no indication anything went wrong.
        if (!alive) return;
        showErrorToast({
          title: t(
            "subIBReport_filterOptionsLoadError",
            "Couldn't load filter options",
          ),
          description: t(
            "subIBReport_filterOptionsLoadErrorDescription",
            "Some filters may be unavailable. Refresh the page to try again.",
          ),
        });
      });
    return () => {
      alive = false;
    };
  }, [filterOptionsApi, t]);

  const mockSelectOptions = useMemo(
    () => ({
      subIb: roster.map((s) => ({ value: s.id, label: s.name })),
    }),
    [roster],
  );

  const selectOptions = useMemo(() => {
    const subIb = filterOptionsApi
      ? (remoteSelectOptions?.subIb ?? [])
      : mockSelectOptions.subIb;
    const statusLabel = (option: SharedRebateProgramsOption): string => {
      switch (option.value.toLowerCase()) {
        case "active":
          return t("subIBReport_statusActive", "Active");
        case "archived":
          return t("subIBReport_statusArchived", "Closed");
        default:
          return option.label;
      }
    };
    return {
      subIb,
      status: STATIC_STATUS_OPTIONS.map((option) => ({
        ...option,
        label: statusLabel(option),
      })),
    };
  }, [filterOptionsApi, remoteSelectOptions, mockSelectOptions, t]);

  const fetchProgramDetailPatch = resources.programDetail?.api;

  // Memoized: a fresh object here on every render — even with tableApi/
  // statsApi/statsShouldAutoRun themselves stable — would still make the
  // base report context's auto-run effect re-run on every unrelated local
  // UI-state change (opening the drawer/filter/wizard, filter options
  // resolving, …). Since the table's shouldAutoRun always returns true,
  // that retrigger fires another network table request every time.
  const baseResources = useMemo(
    () => ({
      // `showLoadingOnRefetch` so a re-run reports "loading" instead of sitting on
      // the previous data silently. `ReloadScopeBridge` reads that status to know
      // which resources are actually in flight — which is the only reliable way to
      // tell, since each watches a different set of params.
      table: {
        api: tableApi,
        shouldAutoRun: ALWAYS_AUTO_RUN,
        showLoadingOnRefetch: true,
      },
      stats: {
        api: statsApi,
        shouldAutoRun: statsShouldAutoRun,
        showLoadingOnRefetch: true,
      },
      csv: { api: csvApi },
    }),
    [tableApi, statsApi, statsShouldAutoRun, csvApi],
  );

  const uiValue = useMemo<SharedRebateProgramsUiState>(
    () => ({
      wizardOpen,
      openWizard: () => setWizardOpen(true),
      closeWizard: () => {
        setWizardOpen(false);
        setWizardPresetSubId(null);
        setPendingCreateResume(null);
      },
      wizardPresetSubId,
      selectedProgramId,
      openProgramDetails: (id) => setSelectedProgramId(id),
      closeProgramDetails: () => setSelectedProgramId(null),
      registerCreatedProgram,
      archiveProgram,
      requestArchiveProgram,
      filterOpen,
      openFilter: () => setFilterOpen(true),
      closeFilter: () => setFilterOpen(false),
      capModalOpen,
      openCapModal: () => setCapModalOpen(true),
      closeCapModal: () => setCapModalOpen(false),
      verifyEmailOpen,
      openVerifyEmail: () => setVerifyEmailOpen(true),
      closeVerifyEmail: () => {
        setVerifyEmailOpen(false);
        gatedContinuation.current = null;
      },
      verifyEmail,
      capLimit,
      statusPending,
      noRebateAccountOpen,
      defaultSetPendingOpen,
      closeDefaultSetPending: () => setDefaultSetPendingOpen(false),
      createErrorOpen,
      closeCreateError: () => setCreateErrorOpen(false),
      closeNoRebateAccount: () => setNoRebateAccountOpen(false),
      openNoRebateAccount: () => setNoRebateAccountOpen(true),
      requestCreateProgram,
      createProgram: resources.create?.api,
      createStepUp: resources.create?.stepUp,
      pendingCreateResume,
      totalProgramsUnfiltered: allPrograms.length,
      splitEmptyState,
      totalRebatePrograms,
      totalRebateProgramsPending,
      refreshTotalRebatePrograms,
      allPrograms,
      roster,
      fetchProgramDetailPatch,
      dismissRejectedNotification: resources.rejectionNotification?.dismiss,
      masterRebateAccount,
    }),
    [
      wizardOpen,
      wizardPresetSubId,
      selectedProgramId,
      registerCreatedProgram,
      archiveProgram,
      requestArchiveProgram,
      filterOpen,
      capModalOpen,
      verifyEmailOpen,
      verifyEmail,
      capLimit,
      statusPending,
      noRebateAccountOpen,
      defaultSetPendingOpen,
      createErrorOpen,
      requestCreateProgram,
      resources.create,
      pendingCreateResume,
      splitEmptyState,
      totalRebatePrograms,
      totalRebateProgramsPending,
      refreshTotalRebatePrograms,
      allPrograms,
      roster,
      fetchProgramDetailPatch,
      resources.rejectionNotification,
      masterRebateAccount,
    ],
  );

  return (
    <SharedRebateProgramsUiContext.Provider value={uiValue}>
      <BaseSharedRebateProgramsProvider
        deriveParams={deriveParams}
        resources={baseResources}
        selectOptions={selectOptions}
        initialState={{
          pageSize: PAGE_SIZE,
          dateRange: getAllTimeDateRange(),
        }}
      >
        <ReloadScopeBridge>{children}</ReloadScopeBridge>
      </BaseSharedRebateProgramsProvider>
    </SharedRebateProgramsUiContext.Provider>
  );
};

export const useSharedRebateProgramsContext = () => {
  const { state, actions, resources, options, runResource } =
    useBaseSharedRebateProgramsContext();
  const ui = useSharedRebateProgramsUi();

  const tableResource = resources.table;
  const rows = tableResource.data?.rows ?? [];
  const totalCount = tableResource.data?.totalCount ?? 0;
  // With the empty state split, an empty table can't be classified until the
  // total is known — hold the loading state rather than flash the wrong one.
  const awaitingTotal =
    ui.splitEmptyState && ui.totalRebateProgramsPending && totalCount === 0;
  const isLoading =
    tableResource.status === "loading" ||
    tableResource.status === "idle" ||
    awaitingTotal;
  const isError = tableResource.status === "error";

  const statsResource = resources.stats;
  const stats = statsResource.data;
  const isStatsLoading =
    statsResource.status === "loading" || statsResource.status === "idle";
  const isStatsError = statsResource.status === "error";

  // Export CSV is served by the API (GET /v1/master/rebate-programs/export), so
  // there is no client-side row set any more. The `csvRows` this replaced could
  // only ever be correct while the whole dataset sat in memory — once the table
  // is paginated it would have silently exported just the current page.
  // Only the loading flag is exposed: the csv api reports its own failure via a
  // toast and resolves, so the resource never settles into "error" and an
  // `isCsvError` here would be dead state.
  const isCsvLoading = resources.csv.status === "loading";

  /**
   * Pulls the newly created program into view (AC 1). The session-injection path
   * (`registerCreatedProgram`) cannot do this on its own: when a real `table`
   * resource is injected the rows come from the API and never consult
   * `created.programs`, so the server has to be asked again.
   *
   * Paging is reset first because the default sort puts a never-paid program at
   * the top of page 1 — a user who created from page 3 would otherwise not see
   * the row they just made. The explicit `runResource` is then *conditional*:
   * the table's shouldAutoRun is always-true, so changing the page already
   * refetches, and calling both would fire two requests. Skipping the reset
   * entirely is the worse bug though — on page 1 the value doesn't change, no
   * auto-run fires, and nothing would refresh at all.
   *
   * Filters, search and sort are deliberately left alone: silently clearing a
   * user's filters is more surprising than a new row that doesn't match them.
   */
  const refreshAfterCreate = () => {
    if (state.page !== 0) {
      actions.setPage(0);
    } else {
      runResource("table");
    }
    // Active-program count and the client totals all move on a create.
    runResource("stats");
    ui.refreshTotalRebatePrograms();
  };

  return {
    state,
    actions,
    options,
    rows,
    totalCount,
    isLoading,
    isError,
    // Retry for the table's error state.
    retryTable: () => runResource("table"),
    refreshAfterCreate,
    hasNoRebatePrograms: ui.splitEmptyState && ui.totalRebatePrograms === 0,
    stats,
    isStatsLoading,
    isStatsError,
    isCsvLoading,
    /** Triggers the CSV export; the download happens inside the resource's api. */
    exportCsv: () => runResource("csv"),
    ...ui,
  };
};
